// Scrubbing helpers shared by the Sentry context integration.
//
// Everything that leaves the browser passes through here: an event may only carry
// what is needed to triage a failure. Request/response bodies, headers, tokens,
// query values and URL fragments never qualify — a query value is user-typed
// search text, and a fragment can carry an OAuth token.

import type { Breadcrumb, Event } from '@sentry/react';

// Sentry truncates tag values past 200 characters server-side, at an arbitrary
// point. Anything written to a tag is cut here instead, so the value is never
// silently mangled halfway through a route or a code list.
export const MAX_TAG_VALUE_LENGTH = 200;

// Per-field limits for the JSON:API errors copied onto the event.
export const MAX_API_ERRORS = 5;
export const MAX_CODE_LENGTH = 100;
export const MAX_POINTER_LENGTH = 250;
export const MAX_DETAIL_LENGTH = 500;

// Anything that looks like an address is redacted from free text: a backend
// `detail` routinely echoes the offending value ("user with email x@y.z exists"),
// and user email must never reach Sentry.
const EMAIL_PATTERN = /[^\s@<>()[\]",;:]+@[^\s@<>()[\]",;:]+\.[a-z]{2,}/gi;

const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>]+/gi;

export const truncate = (value: string, max: number): string =>
  value.length <= max ? value : `${value.slice(0, max - 1)}…`;

/** `url` without its query string or fragment. Works on absolute and relative URLs. */
export const sanitizeUrl = (url: string): string => url.split('#')[0].split('?')[0];

/** Free text with embedded URLs stripped of query/fragment and emails redacted. */
export const sanitizeText = (text: string): string =>
  text.replace(URL_IN_TEXT_PATTERN, sanitizeUrl).replace(EMAIL_PATTERN, '[email]');

const sanitizeOptionalUrl = (value: unknown): string | undefined =>
  typeof value === 'string' ? sanitizeUrl(value) : undefined;

/**
 * Rebuild a breadcrumb with only the fields that are safe to send.
 *
 * XHR/fetch breadcrumbs are rebuilt from an allow-list (`method`, `url`,
 * `status_code`) so nothing the SDK — or a future version of it — puts on `data`
 * can leak a body or a header. Identifiers are *kept* in breadcrumb URLs: they
 * make the trail readable, and they are excluded from the fingerprint only.
 */
export const sanitizeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  const { category, data, message } = breadcrumb;
  const sanitized: Breadcrumb = { ...breadcrumb };

  if (typeof message === 'string') {
    sanitized.message = sanitizeText(message);
  }

  if (category === 'xhr' || category === 'fetch') {
    sanitized.data = {
      method: data?.method,
      url: sanitizeOptionalUrl(data?.url),
      status_code: data?.status_code,
    };
    return sanitized;
  }

  if (category === 'navigation') {
    sanitized.data = {
      from: sanitizeOptionalUrl(data?.from),
      to: sanitizeOptionalUrl(data?.to),
    };
    return sanitized;
  }

  if (category === 'console') {
    // `data.arguments` is the raw, structured duplicate of `message` — anything a
    // developer logged (a whole response, a token) verbatim. `message` alone is
    // enough for the trail.
    sanitized.data = { logger: data?.logger };
    return sanitized;
  }

  return sanitized;
};

/**
 * Strip query/fragment from the page URL and from the `Referer` header, and drop
 * every request header outside the allow-list — `Cookie` and `Authorization` above
 * all, but also anything a future SDK version might start attaching.
 *
 * `User-Agent` stays: it is what Sentry parses server-side into the automatic
 * browser and operating-system contexts, so removing it would blind triage to
 * which browser/OS the failure came from.
 */
const sanitizeRequest = (event: Event): void => {
  const { request } = event;
  if (!request) {
    return;
  }

  if (typeof request.url === 'string') {
    request.url = sanitizeUrl(request.url);
  }

  const { Referer: referer, 'User-Agent': userAgent } = request.headers ?? {};
  request.headers = {
    ...(typeof referer === 'string' ? { Referer: sanitizeUrl(referer) } : {}),
    ...(typeof userAgent === 'string' ? { 'User-Agent': userAgent } : {}),
  };
};

/** Apply every scrubbing rule to an outgoing event, in place. */
export const sanitizeEvent = (event: Event): void => {
  sanitizeRequest(event);

  if (typeof event.message === 'string') {
    event.message = sanitizeText(event.message);
  }

  // Every exception in the chain, not just the one that was thrown: a third-party
  // library is free to interpolate a URL into its message, and a cause is as
  // visible in Sentry as the error it caused.
  event.exception?.values?.forEach((exception) => {
    if (typeof exception.value === 'string') {
      exception.value = sanitizeText(exception.value);
    }
  });

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(sanitizeBreadcrumb);
  }
};
